import { useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { ButtonContainer, Loading, ToggleButton } from '../atoms';
import {
  SecureCoursesLink,
  DisplayTable,
  DisplayTableRow,
  SubCoursesList
} from '../molecules';
import type { TCourse, TCourses } from '../../common/types/Course';
import { z } from 'zod';
import { APIEndPoints, Request } from '../../apis';
import { UserContext } from '../../contexts/UserContext';
import { FormattedCurrency } from '../../common/utils/formatters';
import type { TSortOrder } from '../../common/sort';
import { sortCourses } from '../../common/sort';

type TCourses = [TCourse] | null | [];

const CoursesContainer = styled.div`
  display: grid;
  grid-gap: 30px;
`;

const CourseWrapper = styled.div(
  ({ theme }) => css`
    background-color: ${theme.colors.white};
  `
);

const Course = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-gap: 30px;
    grid-template-columns: auto 7rem;
    align-items: center;
    padding: 30px 30px 30px;
    transition: 0.35s ease;

    @media (hover: hover) {
      &:hover {
        background-color: ${theme.colors.main.light};
      }
    }
  `
);
const FormButtons = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 20px;
`;

const CourseHeading = styled.h2`
  margin-bottom: 0;
`;

const CourseCost = styled.span(
  ({ theme }) => css`
    font-size: ${theme.fonts.sizes.h5};
    color: ${theme.colors.darkGrey};
    background-color: ${theme.colors.warmGrey};
    border-radius: 0 20px 20px 0;
    padding: 2px 8px 2px 4px;
    margin-left: 5px;
  `
);

const CoursesList = () => {
  const [coursesDetails, setCoursesDetails] = useState<TCourses>(null);

  const { user } = useContext(UserContext);
  const merchantId = user.merchantId;
  const [sortOrder, setSortOrder] = useState<TSortOrder>('ASC');

  const getSortedCourses = (coursesDetails: TCourses): TCourses => {
    if (coursesDetails === null) {
      return [];
    }
    return sortCourses(coursesDetails, sortOrder);
  };

  useEffect(() => {
    if (coursesDetails !== null) {
      const sortedCourses = getSortedCourses(coursesDetails);
      setCoursesDetails(sortedCourses);
    }
  }, [sortOrder]);

  useEffect(() => {
    setCoursesDetails(null);
    const getCourses = async () => {
      const courses = await Request.safeGet({
        path: `${APIEndPoints.courses}/${merchantId}?status=ACTIVE&access_token`,
        schema: z.any()
      });
      if (courses.type === 'right') {
        setCoursesDetails(courses.value);
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
      } else {
        setCoursesDetails([]);
      }
    };
    getCourses();
  }, [merchantId]);

  return (
    <CoursesContainer>
      <FormButtons
        horizontalSpacing="6px"
        verticalSpacing="0"
        justify="flex-end"
      >
        <ToggleButton
          alternative={sortOrder === 'ASC' ? true : false}
          onClick={() => {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
          }}
        >
          Sort
          {/* {isFilterOpen ? 'Ascending' : 'Decending'} By Name */}
        </ToggleButton>
      </FormButtons>
      {coursesDetails === null && <Loading />}
      {coursesDetails !== null && coursesDetails.length === 0 && (
        <DisplayTable colWidths={['auto']} colWidthsMd={['auto']}>
          <DisplayTableRow
            type="div"
            cells={[
              {
                contentType: 'error',
                content: 'No Courses found'
              }
            ]}
          />
        </DisplayTable>
      )}
      {coursesDetails !== null && coursesDetails.length > 0 && (
        <>
          {coursesDetails.map((courseDetails) => {
            const { id, cost, polName, subCategories } = courseDetails;

            return (
              <CourseWrapper key={id}>
                <Course>
                  <CourseHeading>
                    {polName}
                    {cost && (
                      <CourseCost>
                        <FormattedCurrency amount={cost} noDecimals />
                      </CourseCost>
                    )}
                  </CourseHeading>
                  <SecureCoursesLink
                    courseDetails={courseDetails}
                    size="2.5rem"
                  />
                </Course>
                {subCategories && subCategories.length > 0 && (
                  <SubCoursesList
                    subCourses={subCategories}
                    courseDetails={courseDetails}
                  />
                )}
              </CourseWrapper>
            );
          })}
        </>
      )}
    </CoursesContainer>
  );
};

export { CoursesList };
